import React, { useState, useMemo } from 'react';
import { XCircle, CloudRain, Cloud, CloudSun, Sun, Eye, TrendingDown } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import { localize } from '../../data/i18nData';
import useGameStore from '../../store/gameStore';
import { EMPTY_OBJECT } from '../../store/stableEmpties';
import { resolveClient } from '../../utils/resolveClient';
import ClientLogo from '../crm/ClientLogo';

/**
 * 03/06/2026 Phase 3 — enhanced ActiveContractsList :
 *  - Client logo on each row (replaces the generic icon)
 *  - "Météo client" emoji based on missedDeliveries count
 *  - Smoother progress bar with day labels
 *  - Animation d'arrivée discrete via Tailwind utility classes
 */
function getClientWeather(missed = 0) {
  if (missed === 0) return { Icon: Sun, label: 'Excellent', color: '#fbbf24', tint: 'bg-amber-500/15' };
  if (missed === 1) return { Icon: CloudSun, label: 'Bon', color: '#10b981', tint: 'bg-emerald-500/15' };
  if (missed === 2) return { Icon: Cloud, label: 'Tendu', color: '#f59e0b', tint: 'bg-amber-500/15' };
  return { Icon: CloudRain, label: 'Mécontent', color: '#dc2626', tint: 'bg-rose-500/15' };
}

export function ActiveContractsList({ activeContracts, vehicles, drivers, onCancel }) {
  const { t } = useTranslation();
  const clientLoyalty = useGameStore(s => s.clientLoyalty || EMPTY_OBJECT);
  // 11/02/2026 — Retour user prod : filtre dédié pour identifier les
  // contrats issus du démarchage stratégique (Veille concurrentielle).
  // Chaque contrat gagné via challenge porte `origin: 'challenge'` côté
  // marketIntelSlice. Pour les autres contrats, origin est undefined.
  const [originFilter, setOriginFilter] = useState('all'); // 'all' | 'challenge'

  const challengeCount = useMemo(
    () => (activeContracts || []).filter(c => c.origin === 'challenge').length,
    [activeContracts]
  );
  // 28/09/2026 — Plafond d'équilibre de marché : contrats renégociés.
  const pressuredCount = useMemo(
    () => (activeContracts || []).filter(c => c.marketPressure).length,
    [activeContracts]
  );

  const filteredContracts = useMemo(() => {
    if (originFilter === 'challenge') {
      return (activeContracts || []).filter(c => c.origin === 'challenge');
    }
    return activeContracts || [];
  }, [activeContracts, originFilter]);

  if ((activeContracts || []).length === 0) return null;

  return (
    <div className="surface-card overflow-hidden" data-testid="active-contracts">
      <div className="flex items-center justify-between px-4 py-3 border-b border-ink-600 gap-2 flex-wrap">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase">
          {t('active_contracts')}
          <span className="font-mono text-[10px] text-slate-500 tabular ml-2">[{filteredContracts.length}{originFilter === 'challenge' ? `/${activeContracts.length}` : ''}]</span>
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {pressuredCount > 0 && (
            <span
              className="inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-orange-500/15 text-orange-300 border border-orange-500/40"
              data-testid="market-tension-header-badge"
              title={t('market_tension_header_title')}
            >
              <TrendingDown className="w-3 h-3" />
              {t('market_tension_header', { count: pressuredCount })}
            </span>
          )}
          {/* 11/02/2026 — Filtre Origine (Tous / Veille concurrentielle) */}
          {challengeCount > 0 && (
            <div className="inline-flex items-center gap-1 p-0.5 rounded-md bg-ink-700/40 border border-ink-600" data-testid="active-contracts-origin-filter">
              <button
                onClick={() => setOriginFilter('all')}
                className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors ${originFilter === 'all' ? 'bg-accent text-white' : 'text-slate-400 hover:text-slate-200'}`}
                data-testid="active-contracts-filter-all"
              >
                Tous
              </button>
              <button
                onClick={() => setOriginFilter('challenge')}
                className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors inline-flex items-center gap-1 ${originFilter === 'challenge' ? 'bg-accent text-white' : 'text-slate-400 hover:text-slate-200'}`}
                data-testid="active-contracts-filter-challenge"
                title="Contrats issus de la veille concurrentielle"
              >
                <Eye className="w-3 h-3" />
                Veille
                <span className="font-mono text-[9px] opacity-80">({challengeCount})</span>
              </button>
            </div>
          )}
          <span className="data-stat text-[11px] text-money font-mono font-bold">
            {formatMoney(filteredContracts.reduce((s, c) => s + (c.dailyRevenue || 0), 0))}/jour
          </span>
        </div>
      </div>
      <div className="p-3 space-y-2">
        {filteredContracts.length === 0 ? (
          <div className="text-center py-6 text-xs text-slate-500" data-testid="active-contracts-empty-filtered">
            Aucun contrat issu de la veille concurrentielle pour l&apos;instant.
          </div>
        ) : filteredContracts.map(rawC => {
          const c = localize('contract', rawC);
          const total = c.frequency === 'ponctuel' ? (c.duration || 1) : (c.duration || 30);
          const elapsed = Math.max(0, total - (c.daysRemaining || 0));
          const pct = total > 0 ? Math.min(100, Math.round((elapsed / total) * 100)) : 0;
          const linkedVehicle = vehicles.find(v => v.id === c.vehicleId);
          const linkedDriver = drivers.find(d => d.id === c.driverId);
          const client = resolveClient(c);
          const missed = c.missedDeliveries || 0;
          const weather = getClientWeather(missed);
          const Weather = weather.Icon;
          const isFromChallenge = c.origin === 'challenge';

          return (
            <div
              key={c.instanceId}
              className="border border-ink-600 bg-ink-700/30 rounded-md p-3 hover:bg-ink-700/50 transition-colors"
              data-testid={`dashboard-active-contract-${c.instanceId}`}
            >
              <div className="flex items-start gap-3">
                <ClientLogo client={client} clientId={c.clientId} category={c.category} size="md" className="flex-shrink-0 shadow" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-100 truncate">{c.name}</p>
                    {/* 11/02/2026 — Badge "Veille" pour les contrats gagnés via démarchage */}
                    {isFromChallenge && (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/40"
                        data-testid={`contract-badge-challenge-${c.instanceId}`}
                        title="Contrat gagné via la Veille concurrentielle"
                      >
                        <Eye className="w-3 h-3" />
                        Veille
                      </span>
                    )}
                    {/* 28/09/2026 — Plafond d'équilibre de marché : contrat renégocié */}
                    {c.marketPressure && (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-300 border border-orange-500/40"
                        data-testid={`contract-badge-market-tension-${c.instanceId}`}
                        title={t('market_tension_badge_title', { pct: c.marketPressure.reductionPct || 0, margin: c.marketPressure.grossMarginPct || 0 })}
                      >
                        <TrendingDown className="w-3 h-3" />
                        {t('market_tension_badge')} −{c.marketPressure.reductionPct || 0} %
                      </span>
                    )}
                    {/* Météo client */}
                    <span
                      className={`inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${weather.tint}`}
                      style={{ color: weather.color }}
                      data-testid={`contract-weather-${c.instanceId}`}
                      title={`Météo client : ${weather.label} (${missed} livraison(s) manquée(s))`}
                    >
                      <Weather className="w-3 h-3" />
                      {weather.label}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5 truncate">
                    {c.clientName && <span className="text-slate-400">{c.clientName} · </span>}
                    {linkedVehicle ? `${linkedVehicle.brand} ${linkedVehicle.model}` : '—'}
                    {linkedDriver ? ` · ${linkedDriver.name}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-base font-black text-money font-mono leading-none" data-testid={`dashboard-mission-days-${c.instanceId}`}>
                      {c.daysRemaining}<span className="text-[10px] text-slate-500 font-normal">/{total}j</span>
                    </p>
                    <p className="text-[9px] text-money font-mono font-bold mt-0.5">
                      {c.marketPressure && c.originalDailyRevenue > c.dailyRevenue && (
                        <span className="text-slate-500 line-through font-normal mr-1" data-testid={`contract-original-daily-${c.instanceId}`}>{formatMoney(c.originalDailyRevenue)}</span>
                      )}
                      {formatMoney(c.dailyRevenue)}/j
                    </p>
                  </div>
                  <button
                    onClick={() => onCancel(c)}
                    className="text-[10px] px-2 py-1 rounded-sm border border-danger/40 text-danger hover:bg-danger/10 transition-colors font-mono uppercase tracking-wider flex items-center gap-1"
                    data-testid={`cancel-contract-${c.instanceId}`}
                    title={t('cancel_contract')}
                  >
                    <XCircle className="w-3 h-3" />
                    {t('cancel_contract')}
                  </button>
                </div>
              </div>
              <div
                className="mt-2 h-1.5 rounded-full bg-ink-700/50 overflow-hidden"
                data-testid={`dashboard-mission-progress-${c.instanceId}`}
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div
                  className="h-full bg-money transition-all duration-700"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500">
                <span>{pct}% accompli{missed > 0 && <span className="text-rose-400 ml-1"> · {missed} miss</span>}</span>
                <span className="data-stat text-money font-mono font-bold">
                  Gains projetés : {formatMoney(c.daysRemaining * c.dailyRevenue)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
